import type { KeyboardEvent, ReactNode } from 'react';
import { CalendarClock, CalendarDays } from 'lucide-react';
import { cn } from '../../lib/cn';
import { formatDatum } from '../../lib/format';
import { TONE_TEXT, type Tone } from '../../lib/tone';
import { useControlled } from '../../lib/use-controlled';
import { Checkbox } from '../ui/checkbox';
import { Icon, type LucideIcon } from '../ui/icon';

export interface ListRowProps {
  title: ReactNode;
  /** Unter dem Titel, höchstens zwei Zeilen: die Beschreibung einer Aufgabe. */
  description?: ReactNode;
  meta?: ReactNode;
  metaIcon?: LucideIcon;
  /**
   * Fälligkeit einer Aufgabe (`YYYY-MM-DD`), vorn in der Meta-Zeile:
   * „Überfällig, fällig am 20.10.2026“ in danger, „Fällig heute“ in warning,
   * sonst „Fällig am …“. Ist die Aufgabe erledigt, ohne Ton.
   */
  faellig?: string | null;
  /** Runde Checkbox vorn, für Aufgaben. */
  checkable?: boolean;
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /** Sperrt die Checkbox: solange das Erledigen läuft, oder wenn sich Erledigtes nicht zurückholen lässt. */
  disabled?: boolean;
  /** Statt Checkbox, z. B. ein Avatar. */
  leading?: ReactNode;
  /** Menü, Bearbeiten, eine Zahl. */
  trailing?: ReactNode;
  /**
   * Die ganze Zeile als Klickziel (den Eintrag öffnen): Hover, Rolle, Fokusring,
   * Enter und Leertaste — wie `Card onClick`.
   *
   * Nicht mit `checkable` zusammen verwenden: Die runde Checkbox wäre dann ein
   * Klickziel im Klickziel, und ein Haken würde die Zeile mit öffnen. Eine
   * Aufgabe hakt man ab, einen Eintrag öffnet man.
   *
   * Der Grund für die Angabe: Die Liste „Kürzlich abgeschlossen“ in „Meine
   * Arbeit“ war ohne sie eine Einbahnstraße — ein versehentlich abgelehntes
   * Projekt stand darin, ließ sich aber nicht mehr öffnen.
   */
  onClick?: () => void;
  className?: string;
}

/** Heute als `YYYY-MM-DD` in Ortszeit. */
function heute(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Ganze Tage von `von` bis `bis`, beide `YYYY-MM-DD` (Kalendertage, ohne Sommerzeit-Versatz). */
function tageZwischen(von: string, bis: string): number {
  const utc = (tag: string) => Date.UTC(Number(tag.slice(0, 4)), Number(tag.slice(5, 7)) - 1, Number(tag.slice(8, 10)));
  return Math.round((utc(bis) - utc(von)) / 86_400_000);
}

/** „morgen“, „in 12 Tagen“, „gestern“, „vor 3 Tagen“. */
function restlaufzeit(tage: number): string {
  if (tage === 1) return 'morgen';
  if (tage === -1) return 'gestern';
  return tage > 0 ? `in ${tage} Tagen` : `vor ${-tage} Tagen`;
}

export interface FaelligkeitOptionen {
  /** Erledigt: Datum ohne Ton. */
  erledigt?: boolean;
  /** Restlaufzeit anhängen: „Fällig am 30.11.2026, in 33 Tagen“, „…, morgen“, „…, vor 3 Tagen“. */
  relativ?: boolean;
  /**
   * Vorwarnung: Was in höchstens so vielen Tagen fällig ist, steht in
   * `warning`. Aus den Einstellungen der App (Radar:
   * `deadline_warning_weeks`), nicht fest im Code. Ohne Angabe nur „heute“.
   */
  vorwarnungTage?: number;
  /**
   * Vergangenes ruhig: „Abgelaufen am …“ ohne Ton statt „Überfällig, fällig
   * am …“ in `danger`. Für Fristen, die man verstreichen lassen darf (eine
   * Ausschreibung, die vorbei ist); eine Aufgabe ist dagegen überfällig.
   */
  vergangenRuhig?: boolean;
  /**
   * Ein Termin statt einer Frist (Veranstaltung im Radar): „Termin am …“,
   * heute „Termin heute“, vorbei „Termin war am …“, immer ohne Ton. Ein
   * Termin verlangt nichts; `vorwarnungTage` und `vergangenRuhig` gelten
   * dafür nicht. Icon CalendarDays statt CalendarClock.
   */
  termin?: boolean;
}

/**
 * Wortlaut und Ton einer Fälligkeit. Ein Wortlaut für alle Apps: Aufgaben in
 * CRM und PM, Fristen im Radar der Finanzen.
 */
export function faelligkeit(
  datum: string,
  { erledigt = false, relativ = false, vorwarnungTage, vergangenRuhig = false, termin = false }: FaelligkeitOptionen = {},
): { text: string; tone: Tone } {
  const tag = datum.slice(0, 10);
  const text = formatDatum(`${tag}T00:00:00`);
  if (erledigt) return { text: `Fällig am ${text}`, tone: 'neutral' };
  const tage = tageZwischen(heute(), tag);
  const rest = relativ && tage !== 0 ? `, ${restlaufzeit(tage)}` : '';
  if (termin) {
    if (tage === 0) return { text: 'Termin heute', tone: 'neutral' };
    return { text: tage < 0 ? `Termin war am ${text}${rest}` : `Termin am ${text}${rest}`, tone: 'neutral' };
  }
  if (tage === 0) return { text: 'Fällig heute', tone: 'warning' };
  if (tage < 0) {
    return vergangenRuhig
      ? { text: `Abgelaufen am ${text}${rest}`, tone: 'neutral' }
      : { text: `Überfällig, fällig am ${text}${rest}`, tone: 'danger' };
  }
  const bald = vorwarnungTage !== undefined && tage <= vorwarnungTage;
  return { text: `Fällig am ${text}${rest}`, tone: bald ? 'warning' : 'neutral' };
}

export interface FaelligkeitProps extends FaelligkeitOptionen {
  /** `YYYY-MM-DD` (ein Zeitstempel wird auf den Tag gekürzt). */
  datum: string;
  className?: string;
}

/**
 * Fälligkeit im festen Wortlaut, wie in `ListRow faellig`: „Überfällig,
 * fällig am …“ in danger, „Fällig heute“ in warning, sonst „Fällig am …“.
 * Für Stellen ohne ListRow, etwa den Verlauf im CRM oder Fristen im Radar.
 */
export function Faelligkeit({ datum, className, ...optionen }: FaelligkeitProps) {
  const f = faelligkeit(datum, optionen);
  return (
    <span className={cn('inline-flex items-center gap-1.5', TONE_TEXT[f.tone], className)}>
      <Icon icon={optionen.termin ? CalendarDays : CalendarClock} size={13} />
      {f.text}
    </span>
  );
}

/**
 * Eine Aufgabe oder ein Eintrag: runde Checkbox, Titel, Meta-Zeile, Info
 * rechts. In einer `Card flush`. Aufgaben (`checkable`) brechen am Handy um,
 * statt abgeschnitten zu werden; die Checkbox steht auf Höhe der ersten Zeile.
 */
export function ListRow({
  title,
  description,
  meta,
  metaIcon,
  faellig,
  checkable,
  checked,
  defaultChecked = false,
  onCheckedChange,
  disabled,
  leading,
  trailing,
  onClick,
  className,
}: ListRowProps) {
  const [erledigt, setErledigt] = useControlled(checked, defaultChecked, onCheckedChange);
  // Wie `Card onClick`: Die Prüfung auf `e.target === e.currentTarget` hält
  // Tastendrücke in einem Bedienelement innerhalb der Zeile (ein Menü in
  // `trailing`) davon ab, die Zeile mit auszulösen.
  const klickbar = onClick
    ? {
        role: 'button',
        tabIndex: 0,
        onClick,
        onKeyDown: (e: KeyboardEvent) => {
          if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
          e.preventDefault();
          onClick();
        },
      }
    : {};
  return (
    <div
      {...klickbar}
      className={cn(
        'flex min-h-[54px] gap-3 border-b border-border px-4 py-[9px] last:border-b-0',
        checkable ? 'items-start' : 'items-center',
        onClick && 'cursor-pointer transition-colors duration-150 hover:bg-accent',
        className,
      )}
    >
      {checkable ? (
        <span className="flex h-5 shrink-0 items-center">
          <Checkbox
            shape="circle"
            checked={erledigt}
            onChange={setErledigt}
            disabled={disabled}
            ariaLabel={typeof title === 'string' ? `„${title}“ erledigt` : 'Erledigt'}
          />
        </span>
      ) : (
        leading
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className={cn('font-medium', checkable ? 'break-words md:truncate' : 'truncate', checkable && erledigt && 'text-muted-foreground line-through')}>
          {title}
        </div>
        {description && <p className="line-clamp-2 t-small text-muted-foreground">{description}</p>}
        {(faellig || meta) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 t-small text-muted-foreground">
            {faellig && <Faelligkeit datum={faellig} erledigt={checkable === true && erledigt} />}
            {meta && (
              <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5">
                {metaIcon && <Icon icon={metaIcon} size={13} />}
                {meta}
              </span>
            )}
          </div>
        )}
      </div>
      {trailing && <div className="flex items-center gap-2 t-small text-muted-foreground">{trailing}</div>}
    </div>
  );
}
